import React, { useEffect, useState, useRef } from "react";
import { isEmpty, isNil } from "ramda";
import palette from "../../../../../config/colorPalette";
import * as echarts from "echarts";
import { MenuItem, TextField } from "@mui/material";
import { getCurrentIntensityData } from "../../utils";

const CurrentIntensityVisualization = ({
  measurandsFiltered,
  assetModule,
  configData,
  intensityData,
  data,
  dateTimeList,
  assetSensors,
  assetInfo,
  afterAlert,
  withToolbox = true,
  info,
  intl,
}) => {
  const chartRef = useRef(null);
  const periodRef = useRef(null);
  const maximumRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState(30);
  const [maximum, setMaximum] = useState(1000);
  const [userInteracted, setUserInteracted] = useState(false);
  const [chartData, setChartData] = useState(intensityData);
  const [title, setTitle] = useState("");
  const colors = [
    "#5470C6", // Azul fuerte
    "#91CC75", // Verde suave
    "#EE6666", // Rojo intenso
    "#9A60B4", // Púrpura
    "#FAC858", // Amarillo dorado
    "#EA7CCC", // Rosa claro
    "#FC8452", // Naranja
    "#73C0DE", // Azul cielo
    "#3BA272", // Verde bosque
    "#FF5733", // Naranja rojizo
    "#33FF57", // Verde brillante
    "#5733FF", // Azul eléctrico
    "#FFD700", // Oro
    "#40E0D0", // Turquesa
    "#8A2BE2", // Violeta
    "#FF69B4", // Rosa fuerte
    "#7FFF00", // Chartreuse
    "#DC143C", // Carmesí
    "#00CED1", // Azul oscuro
    "#4682B4", // Acero azul
    "#32CD32", // Verde lima
    "#FF4500", // Naranja rojizo
    "#9400D3", // Violeta oscuro
    "#20B2AA", // Verde aguamarina
    "#FFDAB9", // Melocotón
    "#B22222", // Rojo ladrillo
    "#00FA9A", // Verde primavera
    "#191970", // Azul medianoche
    "#ADFF2F", // Verde amarillo
  ];
  const dateOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };

  function downloadTemporalCSV() {
    const header = [info.asset, info.equipment.name, info.equipment.channels[info.channel - 1].name].join(",");
    let dateTime, combinedData;

    for (let envIndex = 0; envIndex < chartData.length; envIndex++) {
      const moduleData = configData.modules.find((module) => module.name === assetModule);
      const packageData = moduleData.packages.find((packg) => packg.name === chartData[envIndex].measurand);
      const newHeader = [header, `${chartData[envIndex].measurand} (${packageData.unit})`].join(",");
      const subHeaderEnv = [intl.formatMessage({ id: "date" }), info.dateTime].join(",");
      dateTime = info.dateTime;
      const timeData = chartData[envIndex].datetimes;
      const serialData = chartData[envIndex]?.values;
      combinedData = timeData?.map((date, index) => [date.replace(" ", "T"), serialData[index].toFixed(2)]);
      const envCsvData = [newHeader, subHeaderEnv, ...combinedData].join("\n");

      const blob = new Blob([envCsvData], { type: "text/csv" });

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${chartData[envIndex].measurand}_${dateTime}_${intl.formatMessage({
        id:
          chartData[envIndex].measurand === "humidity" || chartData[envIndex].measurand === "temperature"
            ? "meteorological.data"
            : "intensity",
      })}.csv`;

      a.click();

      URL.revokeObjectURL(a.href);
    }
  }

  useEffect(() => {
    setLoading(true);
    const chartElement = document.getElementById(`current-stacked-line-chart`);
    const myChart = echarts.init(chartElement);
    let minData = Number.POSITIVE_INFINITY;
    let maxData = Number.NEGATIVE_INFINITY;
    const yAxisTitle = [];
    const yAxis = [];
    const aux = {};
    const typesMeasurands = {};
    chartData.forEach((series) => {
      const measurandInfo = assetInfo.config.additional_sensor.measurands.find((m) => m.reference === series.measurand);
      const categoryData = configData.additionalSensors.sensors.find(
        (category) => category.name === measurandInfo.category,
      );
      const typeData = categoryData.types.find((type) => type.type === measurandInfo.type);
      const measurandData = typeData.measurands.find((measurand) => measurand.name === measurandInfo.reference);
      setTitle(intl.formatMessage({ id: measurandInfo.category }));
      yAxisTitle.push(`${intl.formatMessage({ id: measurandInfo.type })} (${measurandData.units.main})`);
      if (isNil(typesMeasurands[measurandInfo.type])) {
        typesMeasurands[measurandInfo.type] = [];
      }
      if (!typesMeasurands[measurandInfo.type].includes(measurandInfo.reference))
        typesMeasurands[measurandInfo.type].push(measurandInfo.reference);

      series?.values?.forEach((value) => {
        if (!isNil(chartData)) {
          if (value < minData) minData = value;
          if (value > maxData) maxData = value;
        }
      });
      const dataMinRounded = Math.floor(minData * 100) / 100;
      const dataMaxRounded = Math.ceil(maxData * 100) / 100;
      if (isNil(aux[measurandInfo.reference])) {
        aux[measurandInfo.reference] = [dataMinRounded, dataMaxRounded];
      } else {
        const newMin = Math.min(dataMinRounded, aux[measurandInfo.reference][0]);
        const newMax = Math.max(dataMinRounded, aux[measurandInfo.reference][1]);
        aux[measurandInfo.reference] = [newMin, newMax];
      }
    });
    //const noRepeating = [
    //  /*...new Set(typesMeasurands)*/
    //];
    const noRepeatingTitle = [...new Set(yAxisTitle)];
    const keys = Object.keys(typesMeasurands);
    for (let indexKey = 0; indexKey < keys.length; indexKey++) {
      let min = Infinity,
        max = -Infinity;
      const keyMeasurands = typesMeasurands[keys[indexKey]];
      for (let indexMeasurand = 0; indexMeasurand < keyMeasurands.length; indexMeasurand++) {
        if (aux[keyMeasurands[indexMeasurand]][0] < min) {
          min = aux[keyMeasurands[indexMeasurand]][0];
        }
        if (aux[keyMeasurands[indexMeasurand]][1] > max) {
          max = aux[keyMeasurands[indexMeasurand]][1];
        }
      }
      yAxis.push({
        type: "value",
        min: min,
        max: max,
        name: noRepeatingTitle[indexKey],
        nameRotate: indexKey === 0 ? 90 : 270,
        nameLocation: "middle",
        nameGap: 35,
      });
    }
    const legend = [];
    for (let intensityIndex = 0; intensityIndex < chartData.length; intensityIndex++) {
      legend.push(chartData[intensityIndex].measurand);
    }
    const options = {
      color: colors,
      animation: false,
      tooltip: null,
      legend: { data: legend, top: chartData.length > 3 ? "7%" : "0%", type: "scroll" },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "7%",
        containLabel: true,
      },
      toolbox: withToolbox
        ? {
            feature: {
              dataZoom: {
                yAxisIndex: "all",
                xAxisIndex: "all",
                brushStyle: {
                  color: "#5470C655",
                  borderType: "solid",
                  borderWidth: 1,
                  borderColor: "#5470C6",
                  shadowColor: "#5470C6",
                },
                title: {
                  zoom: intl.formatMessage({ id: "Zoom in" }),
                  back: intl.formatMessage({ id: "Zoom out" }),
                },
              },
              restore: {
                title: intl.formatMessage({ id: "Restore zoom" }),
              },
              saveAsImage: {
                title: intl.formatMessage({ id: "Save as image" }),
              },
              myCustomCSVDownload: {
                show: true,
                title: intl.formatMessage({ id: "Download CSV" }),
                icon: "image://csv-icon80.png",
                onclick: function () {
                  downloadTemporalCSV();
                },
              },
            },
          }
        : {},
      xAxis: {
        type: "time",
        data: chartData[0].datetimes.map((date) => {
          return new Date(date).toLocaleString("es-ES", dateOptions);
        }),
        boundaryGap: false,
        splitLine: {
          show: false,
        },
        axisLine: { onZero: false }, //Fix the bug
        name: intl.formatMessage({ id: "time" }),
        nameLocation: "middle", // Esto centrará el nombre en el eje
        nameGap: 25,
        axisLabel: {
          interval: "auto",
          hideOverlap: true,
          formatter: (value, index, ticks) => {
            const date = new Date(value);

            // Accedemos al eje X de tipo "time" para estimar el rango visible
            const axis = myChart?.getModel?.()?.getComponent("xAxis")?.axis;
            const extent = axis?.scale._extent || [0, 0];
            const rangeMs = Math.abs(extent[1] - extent[0]); // milisegundos visibles

            // Determinar el nivel de detalle según el rango temporal visible
            const oneDay = 24 * 60 * 60 * 1000;
            const oneMonth = 30 * oneDay;
            const oneYear = 365 * oneDay;

            // Formato progresivo según zoom
            if (rangeMs > 3 * oneYear) {
              return date.toLocaleString("es-ES", { year: "numeric" });
            } else if (rangeMs > 6 * oneMonth) {
              return date.toLocaleString("es-ES", {
                month: "short",
                year: "numeric",
              });
            } else if (rangeMs > 5 * oneDay) {
              return date.toLocaleString("es-ES", {
                day: "2-digit",
                month: "short",
              });
            } else if (rangeMs > oneDay / 2) {
              return date.toLocaleString("es-ES", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              });
            } else {
              return date.toLocaleString("es-ES", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              });
            }
          },
        },
      },
      yAxis: yAxis,

      //dataZoom: [
      //  {
      //    type: "inside",
      //    start: 0,
      //    end: 100,
      //    yAxisIndex: [0],
      //    filterMode: "none",
      //  },
      //],
      series: chartData.map((s, seriesIndex) => {
        const measurandInfo = assetInfo.config.additional_sensor.measurands.find((m) => m.reference === s.measurand);
        return {
          data: s.values.map((value, valueIndex) => [s.datetimes[valueIndex], value]),
          name: s.measurand,
          title: s.measurand,
          type: "line",
          symbolSize: 1,
          color: colors[seriesIndex],
          yAxisIndex: keys.indexOf(measurandInfo.type),
        };
      }),
    };
    myChart.setOption(options, { notMerge: false, lazyUpdate: true, silent: true });
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  useEffect(() => {
    if (userInteracted) {
      setUserInteracted(false);
      const sensorCats = [];
      for (let indexMeasurand = 0; indexMeasurand < measurandsFiltered.length; indexMeasurand++) {
        const checkSensor = assetInfo.config.additional_sensor.measurands.some(
          (m) => m.category === measurandsFiltered[indexMeasurand],
        );
        if (checkSensor) sensorCats.push(measurandsFiltered[indexMeasurand]);
      }
      const catRefs = {};

      for (let index = 0; index < sensorCats.length; index++) {
        const mAux = assetInfo.config.additional_sensor.measurands.filter((m) => m.category === sensorCats[index]);
        const refs = mAux.map((m) => m.reference);
        catRefs[sensorCats[index]] = refs;
      }
      getCurrentIntensityData({
        asset: info.asset,
        selectedEquipment: info.equipment,
        channel: info.channel,
        setIntesityData: setChartData,
        afterAlert,
        assetSensors,
        data,
        dateTimeList,
        equipment: info.equipment.equipmentID,
        period,
        maximum,
        setLoadingIntensity: setLoading,
        assetInfo,
        catRefs,
        sensorCats,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInteracted]);

  if (isNil(chartData)) {
    return <div style={{ height: "92vh", display: "flex", justifyContent: "center", alignItems: "center" }}></div>;
  }

  return (
    !isEmpty(chartData) &&
    !isNil(chartData.values) && (
      <div
        style={{
          width: "99%",
          margin: "1rem",
          padding: "0.5rem",
          display: "grid",
          gap: "1rem",
          border: `1px solid ${palette.primary}`,
          pointerEvents: loading ? "none" : "auto",
          position: "relative",
        }}
      >
        <h3 style={{ color: palette.primary }}>{title.toUpperCase()}</h3>
        {loading && (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "rgb(176 176 176 / 60%)",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              position: "absolute",
            }}
          >
            {`${intl.formatMessage({ id: "loading" })}...`}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", width: "85vw" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
            <TextField
              select
              style={{ width: 150 }}
              id={`intensity_period_chart`}
              variant="outlined"
              label={intl.formatMessage({ id: "period" })}
              margin="dense"
              value={period}
              onChange={(e) => {
                setPeriod(e.target.value);
                setUserInteracted(true);
              }}
              ref={periodRef}
            >
              <MenuItem key={7} value={7}>
                7 {intl.formatMessage({ id: "days" })}
              </MenuItem>
              <MenuItem key={30} value={30}>
                30 {intl.formatMessage({ id: "days" })}
              </MenuItem>
              <MenuItem key={60} value={60}>
                60 {intl.formatMessage({ id: "days" })}
              </MenuItem>
              <MenuItem key={90} value={90}>
                90 {intl.formatMessage({ id: "days" })}
              </MenuItem>
              <MenuItem key={365} value={365}>
                1 {intl.formatMessage({ id: "year" })}
              </MenuItem>
              <MenuItem key={Infinity} value={Infinity}>
                {intl.formatMessage({ id: "all.time" })}
              </MenuItem>
            </TextField>
            <TextField
              select
              style={{ marginLeft: "1.10%", marginRight: "1.10%", width: 150 }}
              id={`intensity_max_chart`}
              variant="outlined"
              label={intl.formatMessage({ id: "max.registers" })}
              margin="dense"
              value={maximum}
              onChange={(e) => {
                setMaximum(e.target.value);
                setUserInteracted(true);
              }}
              ref={maximumRef}
            >
              <MenuItem key={1000} value={1000}>
                1000
              </MenuItem>
              <MenuItem key={5000} value={5000}>
                5000
              </MenuItem>
              <MenuItem key={10000} value={10000}>
                10000
              </MenuItem>
              <MenuItem key={25000} value={25000}>
                25000
              </MenuItem>
              <MenuItem key={Infinity} value={Infinity}>
                {intl.formatMessage({ id: "no.limits" })}
              </MenuItem>
            </TextField>
          </div>
          <div style={{ display: "flex", width: "100%" }}>
            <div
              id={`current-stacked-line-chart`}
              ref={chartRef}
              style={{
                width: "100%",
                height: "40vh",
                minWidth: 0,
                position: "relative",
                overflow: "hidden",
              }}
            />
            {/*<MapAnalysis
                  assetData={currentAsset}
                  intl={intl}
                  measureName={measureName}
                  spatialDataList={spatialDataList}
                  temporalDataList={temporalDataList}
                  points={points}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  scales={scales}
                  percentiles={percentiles}
                  differences={differences}
                  equipmentData={selectedEquipment}
                />*/}
          </div>
        </div>
      </div>
    )
  );
};

export default CurrentIntensityVisualization;
